import {
  ANALYTICS_MODE_LABELS,
  ANALYTICS_MODES,
  type AnalyticsMode,
  formatShareBps,
  type TenantModules,
} from "@loot-os/api-contract";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Switch } from "@loot-os/ui/components/switch";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { TenantEventsCard } from "../components/TenantEventsCard";
import { CommercialTermsFields, TenantModulesFields } from "../components/TenantModulesFields";
import { formatDate } from "../lib/format";
import { bpsToPercentText, percentToBps } from "../lib/tenantModulesForm";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

export function SettingsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const { isPlatformAdmin } = useTenantRole();
  const tenant = useQuery(trpc.tenant.get.queryOptions());

  const [sessionTtl, setSessionTtl] = useState<string | null>(null);
  const [opensPaused, setOpensPaused] = useState<boolean | null>(null);
  const [withdrawalsPaused, setWithdrawalsPaused] = useState<boolean | null>(null);

  const update = useMutation(
    trpc.tenant.updateSettings.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.tenant.get.queryOptions().queryKey });
        toast.success("Settings saved.");
      },
    }),
  );
  if (tenant.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading settings…</div>;
  }
  if (tenant.error) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
        {tenant.error.message}
      </div>
    );
  }
  if (!tenant.data) return null;

  const settings = tenant.data.settings;
  const currentTtl = sessionTtl ?? String(settings.sessionTtlMinutes);
  const currentPaused = opensPaused ?? settings.opensPaused;
  const currentWithdrawalsPaused = withdrawalsPaused ?? settings.withdrawalsPaused;
  // Archived = frozen: the server refuses the save, so don't offer it.
  const archived = tenant.data.archivedAt !== null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Tenant-wide configuration for {tenant.data.name}.
        </p>
      </div>

      <Card>
        <CardContent>
          <form
            className="grid gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              update.mutate({
                sessionTtlMinutes: Number.parseInt(currentTtl, 10),
                opensPaused: currentPaused,
                withdrawalsPaused: currentWithdrawalsPaused,
              });
            }}
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="grid gap-2">
                <Label htmlFor="session-ttl">End-user session TTL (minutes, 5–1440)</Label>
                <Input
                  id="session-ttl"
                  type="number"
                  min={5}
                  max={1440}
                  step={1}
                  value={currentTtl}
                  onChange={(e) => setSessionTtl(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="opens-paused"
                checked={currentPaused}
                onCheckedChange={(checked) => setOpensPaused(checked)}
              />
              <Label htmlFor="opens-paused">Pause all opens (kill switch)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="withdrawals-paused"
                checked={currentWithdrawalsPaused}
                onCheckedChange={(checked) => setWithdrawalsPaused(checked)}
              />
              <Label htmlFor="withdrawals-paused">
                Pause all withdrawals: your API and your storefront (cancels still go through)
              </Label>
            </div>
            <div>
              <Button type="submit" disabled={update.isPending || archived}>
                {update.isPending ? "Saving…" : "Save settings"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Currency naming used to live here, and staff still look for it here. */}
      <p className="text-sm text-muted-foreground">
        The currency — its name, symbol and how many cents make one unit — belongs to each
        storefront —{" "}
        <Link
          to="/tenants/$tenantId/storefront/payments"
          params={{ tenantId }}
          search={{ tab: "currency" }}
          className="text-primary underline"
        >
          Storefront › Payments
        </Link>
        , per environment.
      </p>

      <ModulesCard
        tenantId={tenantId}
        modules={tenant.data.moduleSwitches}
        editable={isPlatformAdmin && !archived}
      />
      <CommercialTermsCard
        tenantId={tenantId}
        storefrontProfitShareBps={settings.storefrontProfitShareBps}
        shopifyPlatformShareBps={settings.shopifyPlatformShareBps}
        editable={isPlatformAdmin && !archived}
      />

      {isPlatformAdmin && (
        <>
          <IdentityVerificationCard tenantId={tenantId} archived={archived} />
          <AnalyticsModeCard tenantId={tenantId} archived={archived} />
          <TenantEventsCard tenantId={tenantId} archived={archived} />
          <LifecycleCard
            tenantId={tenantId}
            name={tenant.data.name}
            slug={tenant.data.slug}
            archivedAt={tenant.data.archivedAt}
          />
        </>
      )}
    </div>
  );
}

/**
 * Which parts of Loot OS the tenant has. Platform staff switch them (`platform.tenants.setModules`,
 * the whole map per save); tenant admins see the same list, read-only.
 */
function ModulesCard({
  tenantId,
  modules,
  editable,
}: {
  tenantId: string;
  modules: TenantModules;
  editable: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<TenantModules | null>(null);
  const save = useMutation(
    trpc.platform.tenants.setModules.mutationOptions({
      onSuccess: () => {
        setDraft(null);
        toast.success("Modules saved.");
        void queryClient.invalidateQueries({ queryKey: trpc.tenant.get.queryOptions().queryKey });
        // The Tenants list's row dialog reads the same settings.
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.tenants.list.queryOptions().queryKey,
        });
      },
    }),
  );
  const current = draft ?? modules;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Modules</CardTitle>
        <CardDescription>
          {editable
            ? "Platform staff only. A module that is off leaves this tenant's dashboard and its changes are refused; the storefront's own modules also switch the feature off for shoppers, whatever each environment says."
            : "What this tenant can use. Loot OS staff manage this list."}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid max-w-md gap-4">
        <TenantModulesFields
          idPrefix="settings-module"
          value={current}
          onChange={setDraft}
          disabled={!editable || save.isPending}
        />
        {editable && (
          <div>
            <Button
              type="button"
              disabled={draft === null || save.isPending}
              onClick={() => save.mutate({ tenantId, modules: current })}
            >
              {save.isPending ? "Saving…" : "Save modules"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The platform's shares of the tenant's revenue: staff edit them, tenant admins read them. The
 * storefront share is for reporting only; the Shopify fee is billed as Shopify usage charges.
 */
function CommercialTermsCard({
  tenantId,
  storefrontProfitShareBps,
  shopifyPlatformShareBps,
  editable,
}: {
  tenantId: string;
  storefrontProfitShareBps: number;
  shopifyPlatformShareBps: number;
  editable: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [storefrontDraft, setStorefrontDraft] = useState<string | null>(null);
  const [shopifyDraft, setShopifyDraft] = useState<string | null>(null);
  const save = useMutation(
    trpc.platform.tenants.setCommercialTerms.mutationOptions({
      onSuccess: () => {
        setStorefrontDraft(null);
        setShopifyDraft(null);
        toast.success("Revenue shares saved.");
        void queryClient.invalidateQueries({ queryKey: trpc.tenant.get.queryOptions().queryKey });
        // The Tenants list's row dialog reads the same settings.
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.tenants.list.queryOptions().queryKey,
        });
      },
    }),
  );
  const storefrontText = storefrontDraft ?? bpsToPercentText(storefrontProfitShareBps);
  const shopifyText = shopifyDraft ?? bpsToPercentText(shopifyPlatformShareBps);
  const storefrontBps = percentToBps(storefrontText);
  const shopifyBps = percentToBps(shopifyText);
  const invalid = storefrontBps === null || shopifyBps === null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue shares</CardTitle>
        <CardDescription>
          The storefront share is for reporting only. Loot Boxes for Shopify bills the Shopify fee
          as usage charges on the merchant's Shopify invoice.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {editable ? (
          <form
            className="grid max-w-md gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (storefrontBps === null || shopifyBps === null) return;
              save.mutate({
                tenantId,
                storefrontProfitShareBps: storefrontBps,
                shopifyPlatformShareBps: shopifyBps,
              });
            }}
          >
            <CommercialTermsFields
              idPrefix="settings"
              storefrontProfitShare={storefrontText}
              shopifyPlatformShare={shopifyText}
              onStorefrontProfitShareChange={setStorefrontDraft}
              onShopifyPlatformShareChange={setShopifyDraft}
              disabled={save.isPending}
            />
            {invalid && (
              <p className="text-sm text-destructive">
                Shares are percents from 0 to 100, to two decimals.
              </p>
            )}
            <div>
              <Button
                type="submit"
                disabled={
                  invalid || save.isPending || (storefrontDraft === null && shopifyDraft === null)
                }
              >
                {save.isPending ? "Saving…" : "Save shares"}
              </Button>
            </div>
          </form>
        ) : (
          <dl className="grid max-w-md gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">Storefront profit share</dt>
              <dd className="font-medium">{formatShareBps(storefrontProfitShareBps)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Shopify platform</dt>
              <dd className="font-medium">{formatShareBps(shopifyPlatformShareBps)}</dd>
            </div>
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Platform-only: identity verification (KYC) per environment, required by default. The platform
 * holds the Veriff credential and pays per verification, so a tenant never sets this; staff
 * switch an environment off here when a tenant should withdraw without it.
 */
function IdentityVerificationCard({ tenantId, archived }: { tenantId: string; archived: boolean }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const envs = useQuery(trpc.environments.list.queryOptions());
  const setKycRequired = useMutation(
    trpc.platform.tenants.setKycRequired.mutationOptions({
      onSuccess: (result) => {
        toast.success(`Identity verification ${result.kycRequired ? "required" : "off"}`);
        void queryClient.invalidateQueries({
          queryKey: trpc.environments.list.queryOptions().queryKey,
        });
      },
    }),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Identity verification</CardTitle>
        <CardDescription>
          Platform staff only. Required by default: an unverified shopper is untrusted and every
          withdrawal answers kyc_required until they pass the hosted Veriff flow. Opens, claims and
          top-ups are never gated.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {envs.error && <p className="text-sm text-destructive">{envs.error.message}</p>}
        {envs.data?.map((env) => (
          <div
            key={env.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm"
          >
            <span className="inline-flex items-center gap-2">
              {env.name}
              <Badge variant="outline">{env.type}</Badge>
            </span>
            <div className="flex items-center gap-2">
              <Label htmlFor={`kyc-${env.id}`} className="text-muted-foreground">
                {env.kycRequired ? "Required" : "Off"}
              </Label>
              <Switch
                id={`kyc-${env.id}`}
                checked={env.kycRequired}
                disabled={archived || setKycRequired.isPending}
                onCheckedChange={(required) =>
                  setKycRequired.mutate({ tenantId, environmentId: env.id, required })
                }
              />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Platform-only: whether each environment's storefront records browser analytics events, and
 * under what consent. Consent required by default; a store with no cookie banner then records
 * nothing. Server-side events (opens, claims, purchases) are recorded whatever this says.
 */
function AnalyticsModeCard({ tenantId, archived }: { tenantId: string; archived: boolean }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const envs = useQuery(trpc.environments.list.queryOptions());
  const setMode = useMutation(
    trpc.platform.tenants.setAnalyticsMode.mutationOptions({
      onSuccess: (result) => {
        toast.success(`Analytics: ${ANALYTICS_MODE_LABELS[result.analyticsMode].toLowerCase()}.`);
        void queryClient.invalidateQueries({
          queryKey: trpc.environments.list.queryOptions().queryKey,
        });
      },
    }),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analytics</CardTitle>
        <CardDescription>
          Platform staff only. Whether the storefront records what shoppers do in the browser (page
          views, clicks) and under what consent. Consent required means only after the cookie
          banner's analytics consent, so a store without a banner records nothing; enabled records
          without asking. What happened on the server (opens, claims, purchases) is recorded in
          every mode.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {envs.error && <p className="text-sm text-destructive">{envs.error.message}</p>}
        {envs.data?.map((env) => (
          <div
            key={env.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm"
          >
            <span className="inline-flex items-center gap-2">
              {env.name}
              <Badge variant="outline">{env.type}</Badge>
            </span>
            <Select
              items={ANALYTICS_MODE_LABELS}
              value={env.analyticsMode}
              disabled={archived || setMode.isPending}
              onValueChange={(value) => {
                if (value !== env.analyticsMode) {
                  setMode.mutate({
                    tenantId,
                    environmentId: env.id,
                    mode: value as AnalyticsMode,
                  });
                }
              }}
            >
              <SelectTrigger className="w-44" aria-label={`Analytics mode for ${env.name}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ANALYTICS_MODES.map((mode) => (
                  <SelectItem key={mode} value={mode}>
                    {ANALYTICS_MODE_LABELS[mode]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/** The fulfillment operations records that never cascade from a tenant (platform.tenants.deleteBlockers). */
interface OperationsRecordCounts {
  orders: number;
  orderLines: number;
  accountingRefs: number;
  procurementRequests: number;
  procurementOrderLines: number;
  total: number;
}

const BLOCKER_ROWS: { key: Exclude<keyof OperationsRecordCounts, "total">; label: string }[] = [
  { key: "orders", label: "Orders" },
  { key: "orderLines", label: "Order lines" },
  { key: "accountingRefs", label: "Accounting refs" },
  { key: "procurementRequests", label: "Procurement requests" },
  { key: "procurementOrderLines", label: "Procurement order lines" },
];

/**
 * Platform-only lifecycle controls: archive (a reversible freeze), restore,
 * and — from archived only — a hard delete with the slug typed back and an
 * explicit opt-in over any fulfillment operations records, which the server
 * refuses to cascade on its own.
 */
function LifecycleCard({
  tenantId,
  name,
  slug,
  archivedAt,
}: {
  tenantId: string;
  name: string;
  slug: string;
  /** Wire shape: dates cross tRPC as ISO strings. */
  archivedAt: string | null;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmSlug, setConfirmSlug] = useState("");
  const [purge, setPurge] = useState(false);

  // The tenant's own page, the picker and the platform list all show lifecycle state.
  const invalidateLifecycle = () => {
    void queryClient.invalidateQueries({ queryKey: trpc.tenant.get.queryOptions().queryKey });
    void queryClient.invalidateQueries({ queryKey: trpc.me.tenants.queryOptions().queryKey });
    void queryClient.invalidateQueries({
      queryKey: trpc.platform.tenants.list.queryOptions().queryKey,
    });
  };

  const archive = useMutation(
    trpc.platform.tenants.archive.mutationOptions({
      onSuccess: () => {
        invalidateLifecycle();
        toast.success("Tenant archived.");
      },
    }),
  );
  const restore = useMutation(
    trpc.platform.tenants.restore.mutationOptions({
      onSuccess: () => {
        invalidateLifecycle();
        toast.success("Tenant restored.");
      },
    }),
  );
  // Five counts per open dialog, not per page render.
  const blockers = useQuery(
    trpc.platform.tenants.deleteBlockers.queryOptions(
      deleteOpen && archivedAt !== null ? { tenantId } : skipToken,
    ),
  );
  const remove = useMutation(
    trpc.platform.tenants.delete.mutationOptions({
      onSuccess: () => {
        setDeleteOpen(false);
        toast.success(`Tenant "${name}" deleted.`);
        void queryClient.invalidateQueries({ queryKey: trpc.me.tenants.queryOptions().queryKey });
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.tenants.list.queryOptions().queryKey,
        });
        // Home's beforeLoad clears the tenant scope, which resets every tenant-bound query.
        void navigate({ to: "/" });
      },
    }),
  );

  const counts = blockers.data;
  const canDelete =
    confirmSlug.trim().toLowerCase() === slug &&
    counts !== undefined &&
    (counts.total === 0 || purge) &&
    !remove.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lifecycle</CardTitle>
        <CardDescription>
          Platform staff only. Archiving is reversible; deleting is not.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm">
          {archivedAt ? (
            <>
              <span className="inline-flex items-center gap-2">
                <Badge variant="outline">archived</Badge>
                <span className="text-muted-foreground">since {formatDate(archivedAt)}</span>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={restore.isPending}
                onClick={() => restore.mutate({ tenantId })}
              >
                {restore.isPending ? "Restoring…" : "Restore"}
              </Button>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">
                Archiving takes {name} offline without losing anything: storefronts go dark, API
                keys and shopper sessions are refused, members are locked out, and settings freeze.
              </span>
              <AlertDialog>
                <AlertDialogTrigger
                  render={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={archive.isPending}
                    />
                  }
                >
                  Archive
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive "{name}"?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Every storefront answers "Store not found", every API key and shopper session
                      is refused, members can no longer open the dashboard, and nothing
                      tenant-scoped can change until you restore it. The slug and hostnames stay
                      reserved. Nothing is deleted.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => archive.mutate({ tenantId })}>
                      Archive tenant
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/40 px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            {archivedAt
              ? "Deleting removes the tenant and every row it owns. Its slug and hostnames become available again."
              : "Archive the tenant first — delete is only offered on an archived tenant."}
          </span>
          <AlertDialog
            open={deleteOpen}
            onOpenChange={(open) => {
              setDeleteOpen(open);
              if (!open) {
                setConfirmSlug("");
                setPurge(false);
              }
            }}
          >
            <AlertDialogTrigger
              render={
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={archivedAt === null || remove.isPending}
                />
              }
            >
              Delete
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently deletes every environment, end user, open, ledger entry, box, API
                  key, storefront, shopper, top-up pack, purchase and team member of this tenant.
                  The Stripe connected account is left in place. Type the slug{" "}
                  <span className="font-mono">{slug}</span> to confirm.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <BlockerSummary
                isLoading={blockers.isLoading}
                error={blockers.error}
                counts={counts}
                purge={purge}
                onPurgeChange={setPurge}
              />
              <Input
                type="text"
                value={confirmSlug}
                onChange={(e) => setConfirmSlug(e.target.value)}
                placeholder={slug}
                autoComplete="off"
              />
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={!canDelete}
                  onClick={() =>
                    remove.mutate({ tenantId, confirmSlug, purgeOperationsRecords: purge })
                  }
                >
                  {remove.isPending ? "Deleting…" : "Delete tenant"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}

/** The delete dialog's account of what does not cascade, with the opt-in to purge it. */
function BlockerSummary({
  isLoading,
  error,
  counts,
  purge,
  onPurgeChange,
}: {
  isLoading: boolean;
  error: { message: string } | null;
  counts: OperationsRecordCounts | undefined;
  purge: boolean;
  onPurgeChange: (purge: boolean) => void;
}) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Checking for operations records…</p>;
  }
  if (error) return <p className="text-sm text-destructive">{error.message}</p>;
  if (!counts || counts.total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No fulfillment operations records — nothing else stands in the way.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
      <p>
        This tenant has <strong>{counts.total}</strong> fulfillment operations records (orders,
        accounting, procurement), which never cascade on their own:
      </p>
      <ul className="grid gap-1 sm:grid-cols-2">
        {BLOCKER_ROWS.filter((row) => counts[row.key] > 0).map((row) => (
          <li key={row.key} className="flex justify-between gap-2">
            <span className="text-muted-foreground">{row.label}</span>
            <span className="font-mono">{counts[row.key]}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-3">
        <Switch id="purge-operations" checked={purge} onCheckedChange={onPurgeChange} />
        <Label htmlFor="purge-operations">Also delete these {counts.total} records</Label>
      </div>
    </div>
  );
}
